<!--
  ContactsListEditor — a field's contacts as a list you edit in place: RowsEditor
  over a useContactsEditor, each line a ContactRowEditor when open.

  A closed line shows the contact (with its icon) and its label; an open line
  is the contact's typed box with its label and primary flag under it; the
  add line types a new one. A one-value field holds one contact: it opens on
  it, and adds only once it's removed. Done emits `done` with the delta;
  Cancel emits `cancel`.
-->
<template>
  <RowsEditor
    :editor="editor"
    noun="contact"
    :autofocus="autofocus"
    :footer-when-dirty="footerWhenDirty"
    @done="emit('done', $event)"
    @cancel="emit('cancel')"
  >
    <template #line="{ row }">
      <ContactElement :value="row.cur" fixed />
      <span v-if="row.cur.label && !socials" class="muted">{{ row.cur.label }}</span>
      <span v-if="row.cur.is_primary" class="muted">primary</span>
    </template>

    <template #open="{ row, close, remove, edited }">
      <ContactRowEditor
        :row="row.cur"
        :socials="socials"
        :seed="row.key === seedKey ? seed : ''"
        @update="editor.setRow(row, $event)"
        @enter="close"
        @toggle="close"
      >
        <template #end>
          <span v-if="edited" class="badge is-edited">edited</span>
          <button type="button" class="remove" aria-label="Remove this contact" @click="remove">
            <font-awesome-icon icon="xmark" />
          </button>
        </template>
      </ContactRowEditor>
    </template>

    <template #add="{ open, enter, toggle, autofocus: focusAdd }">
      <ContactRowEditor
        :key="editor.addCount.value"
        :row="editor.pending.value"
        :socials="socials"
        :kind-editable="kind == null"
        :expanded="open"
        :placeholder="placeholder"
        :seed="editor.addCount.value === 0 ? seed : ''"
        :autofocus="focusAdd"
        adding
        @update="editor.setPending"
        @enter="enter"
        @toggle="toggle"
      >
        <template #icon>
          <font-awesome-icon icon="plus" class="plus" fixed-width />
        </template>
        <template #end>
          <span v-if="editor.pendingGiven.value" class="enter">↵ adds</span>
        </template>
      </ContactRowEditor>
    </template>
  </RowsEditor>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue"
import { type Field, getFieldType } from "@auger/core/fields"
import ContactElement from "@auger/ui/components/elements/ContactElement.vue"
import type { ContactRow } from "@auger/ui/utils/contacts"
import RowsEditor from "@/components/rows/RowsEditor.vue"
import {
  contactKindOf,
  type ContactsDelta,
  useContactsEditor,
} from "@/composables/useContactsEditor"
import ContactRowEditor from "./ContactRowEditor.vue"

const props = withDefaults(
  defineProps<{
    /** The field's rows as stored. */
    rows: ContactRow[]
    field: Pick<Field, "type" | "isArray" | "label">
    /** Typed text to start with (a key pressed on the cell). */
    seed?: string
    /** Focus the add line on open — an overlay does, a form on a page doesn't. */
    autofocus?: boolean
    /** Show Cancel / Done only once something changed (a form at rest). */
    footerWhenDirty?: boolean
  }>(),
  { seed: "", autofocus: true, footerWhenDirty: false }
)

const emit = defineEmits<{
  done: [delta: ContactsDelta]
  cancel: []
}>()

const editor = useContactsEditor(props.rows, props.field)

// A typed key starts the line the editor opens on (a one-value field's
// contact), once: reopened later, it shows the contact as it is.
const seedKey = ref(editor.open.value)
onMounted(() => nextTick(() => (seedKey.value = null)))
const kind = contactKindOf(props.field)
const socials = props.field.type === "socials"

/** "Add email…", "Add link…", "Add contact…". */
const placeholder = computed(() => {
  const what = socials
    ? "link"
    : props.field.isArray
      ? getFieldType(props.field.type).label
      : props.field.label
  return `Add ${what?.toLowerCase() ?? "contact"}…`
})
</script>
